/**
 * Danh sách lịch phát thanh — `/lich-phat` (mục "Lịch phát thanh" của menu trái),
 * dựng theo mẫu của hệ thống tỉnh Lào Cai:
 *
 *   - hàng lọc: địa bàn (mặc định cả tỉnh), tên lịch (nút kính lúp), trạng thái
 *     hiệu lực, khoảng ngày, nút "Tải lại danh sách"; hàng dưới nút "Thêm mới lịch"
 *     dẫn tới trang soạn lịch `/lich-phat/moi`;
 *   - bảng: chấm màu hiệu lực, STT, ngày cập nhật, tên lịch, trạng thái, địa bàn,
 *     thiết bị, số ngày phát, người tạo, thao tác "Hủy" (chỉ khi còn hiệu lực).
 *
 * Lọc, đếm, phân trang ở phía máy chủ (`GET /api/v1/schedules`), mặc định 20 dòng.
 * Các trường `hieuLuc`, `tenDiaBan`, `soThietBi`, `soNgayPhat`, `tenNguoiTao`,
 * `updatedAt` là bổ sung theo hợp đồng A0; máy chủ cũ chưa trả thì tính tạm từ
 * dữ liệu sẵn có của dòng.
 */
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { dienVao } from '../ngon-ngu/vi';
import { viDanhSachLichPhat } from '../ngon-ngu/vi-danh-sach-lich-phat';
import {
  lichPhatApi,
  type BoLocLichPhat,
  type HieuLucLich,
  type LichPhatNoiBoRa,
} from '../api/lich-phat-api';
import { LoiApi, thietBiApi, type NutDiaBan, type TrangDuLieu } from '../api/thiet-bi-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { TheTag, TruongLoc } from '../thanh-phan/MauGiaoDien';
import { HopXacNhan } from '../thanh-phan/HopXacNhan';
import { ChonDiaBanCay } from '../thanh-phan/ChonDiaBanCay';
import { BieuTuong } from '../thanh-phan/BieuTuong';
import './danh-sach-lich-phat.css';

const t = viDanhSachLichPhat;

/** Giờ Việt Nam = UTC+7, không có giờ mùa hè. */
const LECH_VN_GIAY = 7 * 3600;
const MOT_NGAY_GIAY = 86_400;
const DS_HIEU_LUC: HieuLucLich[] = ['con_hieu_luc', 'het_hieu_luc', 'da_huy'];

export interface DanhSachLichPhatApi {
  danhSach: (tham: BoLocLichPhat, signal?: AbortSignal) => Promise<TrangDuLieu<LichPhatNoiBoRa>>;
  huy: (id: string) => Promise<unknown>;
  /** Tuỳ chọn để kiểm thử cũ không phải truyền. */
  guiLai?: typeof lichPhatApi.guiLai;
  cayDiaBan: (tham: Record<string, never>, signal?: AbortSignal) => Promise<NutDiaBan[]>;
}

export interface DanhSachLichPhatProps {
  api?: DanhSachLichPhatApi;
}

export const API_DANH_SACH_LICH_MAC_DINH: DanhSachLichPhatApi = {
  danhSach: lichPhatApi.danhSach,
  huy: lichPhatApi.huy,
  guiLai: lichPhatApi.guiLai,
  cayDiaBan: (tham, signal) => thietBiApi.cayDiaBan(tham, signal),
};

/* --------------------------------- Tiện ích --------------------------------- */

/**
 * Ô ngày `YYYY-MM-DD` → epoch giây theo giờ Việt Nam: đầu ngày (00:00) hoặc, với
 * `cuoiNgay`, 24:00 của ngày đó (= 00:00 ngày hôm sau) — đúng hợp đồng A0.
 */
export function ngayVnSangEpoch(ngay: string, cuoiNgay = false): number | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ngay);
  if (!m) return undefined;
  const dau = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 1000 - LECH_VN_GIAY;
  return cuoiNgay ? dau + MOT_NGAY_GIAY : dau;
}

const hai = (n: number): string => String(n).padStart(2, '0');

/** ISO → ['dd/MM/yyyy', 'HH:mm:ss'] theo giờ Việt Nam. */
export function ngayGioVn(iso: string): [string, string] {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return [iso, ''];
  const d = new Date(ms + LECH_VN_GIAY * 1000);
  return [
    `${hai(d.getUTCDate())}/${hai(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`,
    `${hai(d.getUTCHours())}:${hai(d.getUTCMinutes())}:${hai(d.getUTCSeconds())}`,
  ];
}

/** Hiệu lực của dòng: lấy từ máy chủ, máy chủ cũ thì tính theo thời điểm phát muộn nhất. */
export function hieuLucCuaLich(l: LichPhatNoiBoRa, bayGio = Date.now() / 1000): HieuLucLich {
  if (l.hieuLuc) return l.hieuLuc;
  if (l.trangThai === 'da_huy') return 'da_huy';
  let muonNhat = l.ngayKetThucPhat ?? 0;
  for (const n of l.danhSachNgayPhat)
    for (const tp of n.ThoiDiemPhat) muonNhat = Math.max(muonNhat, tp.ThoiGianKetThuc);
  return muonNhat > bayGio ? 'con_hieu_luc' : 'het_hieu_luc';
}

/** Số thiết bị của lịch; 0 = tất cả thiết bị trong địa bàn. */
export function soThietBiCuaLich(l: LichPhatNoiBoRa): number {
  if (l.soThietBi !== undefined) return l.soThietBi;
  return l.phamVi.reduce((tong, p) => tong + (p.CumLoaID?.length ?? 0), 0);
}

export const tenDiaBanCuaLich = (l: LichPhatNoiBoRa): string =>
  l.tenDiaBan ?? l.phamVi.map((p) => p.TenDich ?? p.DichID).join(', ');

export const soNgayCuaLich = (l: LichPhatNoiBoRa): number => l.soNgayPhat ?? l.danhSachNgayPhat.length;

/* ------------------- Ô hiển thị và ô lọc dùng chung với trang khác ------------------- */

/** Ô ngày giờ hai dòng `dd/MM/yyyy` / `HH:mm:ss` theo giờ Việt Nam. */
export function ONgayGio({ iso }: { iso: string }): ReactNode {
  const [ngay, gio] = ngayGioVn(iso);
  return (
    <span className="dslp-khong-xuong">
      {ngay}
      <br />
      {gio}
    </span>
  );
}

/** Thẻ số thiết bị của lịch: "Tất cả" khi phát tới mọi thiết bị trong địa bàn. */
export function OThietBi({ l }: { l: LichPhatNoiBoRa }): ReactNode {
  const so = soThietBiCuaLich(l);
  return <TheTag kieu="ok">{so === 0 ? t.tatCa : so}</TheTag>;
}

/** Ô "N ngày" phát của lịch. */
export function OSoNgayPhat({ l }: { l: LichPhatNoiBoRa }): ReactNode {
  return <span className="dslp-khong-xuong">{dienVao(t.soNgay, { so: soNgayCuaLich(l) })}</span>;
}

/** Ô tìm theo tên lịch kèm nút kính lúp; chỉ gửi từ khoá khi bấm tìm/Enter. */
export function OTimTenLich({
  id,
  giaTri,
  onDoi,
  onTim,
}: {
  id: string;
  giaTri: string;
  onDoi: (v: string) => void;
  onTim: (e?: FormEvent) => void;
}): ReactNode {
  return (
    <TruongLoc nhan={t.tenLich} htmlFor={id}>
      <form className="dslp-tim" onSubmit={onTim}>
        <input
          id={id}
          type="search"
          placeholder={t.nhapTenLich}
          value={giaTri}
          onChange={(e) => onDoi(e.target.value)}
        />
        <button type="submit" className="nut dslp-tim__nut" aria-label={t.timKiem} title={t.timKiem}>
          <BieuTuong ten="tim-kiem" co={16} />
        </button>
      </form>
    </TruongLoc>
  );
}

/** Cặp ô khoảng ngày (ngày bắt đầu → ngày kết thúc), ràng buộc min/max lẫn nhau. */
export function OKhoangNgay({
  tienTo,
  tuNgay,
  denNgay,
  onDoiTu,
  onDoiDen,
  goiY,
}: {
  tienTo: string;
  tuNgay: string;
  denNgay: string;
  onDoiTu: (v: string) => void;
  onDoiDen: (v: string) => void;
  goiY?: string;
}): ReactNode {
  return (
    <div className="dslp-khoang" title={goiY}>
      <TruongLoc nhan={t.ngayBatDau} htmlFor={`${tienTo}-tu-ngay`}>
        <input
          id={`${tienTo}-tu-ngay`}
          type="date"
          value={tuNgay}
          max={denNgay || undefined}
          onChange={(e) => onDoiTu(e.target.value)}
        />
      </TruongLoc>
      <span className="dslp-khoang__mui-ten" aria-hidden="true">
        {t.den}
      </span>
      <TruongLoc nhan={t.ngayKetThuc} htmlFor={`${tienTo}-den-ngay`}>
        <input
          id={`${tienTo}-den-ngay`}
          type="date"
          value={denNgay}
          min={tuNgay || undefined}
          onChange={(e) => onDoiDen(e.target.value)}
        />
      </TruongLoc>
    </div>
  );
}

function OTrangThai({ hl }: { hl: HieuLucLich }): ReactNode {
  if (hl === 'con_hieu_luc')
    return (
      <span className="dslp-tt">
        {t.daDuyet}
        <br />
        <span className="dslp-tt--con">{t.hieuLuc.con_hieu_luc}</span>
      </span>
    );
  return <span className={hl === 'da_huy' ? 'dslp-tt--huy' : 'dslp-tt--het'}>{t.hieuLuc[hl]}</span>;
}

/* --------------------------------- Màn hình --------------------------------- */

export function DanhSachLichPhat({
  api = API_DANH_SACH_LICH_MAC_DINH,
}: DanhSachLichPhatProps = {}): ReactNode {
  const phien = dungPhien();
  const viTri = useLocation();
  // Trang soạn lịch lưu xong chuyển về đây kèm thông báo "Đã lưu N lịch phát".
  const thongBaoDen = (viTri.state as { thongBao?: unknown } | null)?.thongBao;
  const [nguonId, datNguonId] = useState('');
  const [oTim, datOTim] = useState('');
  const [tuKhoa, datTuKhoa] = useState('');
  const [trangThai, datTrangThai] = useState<HieuLucLich | ''>('');
  const [tuNgay, datTuNgay] = useState('');
  const [denNgay, datDenNgay] = useState('');
  const [trang, datTrang] = useState(1);
  const [soDong, datSoDong] = useState<number>(SO_DONG_MAC_DINH);
  const [dangHuy, datDangHuy] = useState<LichPhatNoiBoRa | null>(null);
  const [thongBao, datThongBao] = useState<{ loai: 'ok' | 'loi'; noiDung: string } | null>(
    typeof thongBaoDen === 'string' ? { loai: 'ok', noiDung: thongBaoDen } : null,
  );

  const cay = dungTaiDuLieu<NutDiaBan[]>({
    tai: (signal) => api.cayDiaBan({}, signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [],
  });

  const dangLoc = nguonId !== '' || tuKhoa !== '' || trangThai !== '' || tuNgay !== '' || denNgay !== '';

  const kq = dungTaiDuLieu<TrangDuLieu<LichPhatNoiBoRa>>({
    tai: (signal) =>
      api.danhSach(
        {
          page: trang,
          pageSize: soDong,
          nguonId: nguonId || undefined,
          keyword: tuKhoa || undefined,
          trangThai: trangThai || undefined,
          tuThoiDiem: ngayVnSangEpoch(tuNgay),
          denThoiDiem: ngayVnSangEpoch(denNgay, true),
        },
        signal,
      ),
    laRong: (d) => d.items.length === 0,
    dangLoc,
    phuThuoc: [trang, soDong, nguonId, tuKhoa, trangThai, tuNgay, denNgay],
  });

  /** Đổi bộ lọc thì quay về trang 1. */
  const doiLoc =
    <V,>(dat: (v: V) => void) =>
    (v: V): void => {
      dat(v);
      datTrang(1);
    };

  const tim = (e?: FormEvent): void => {
    e?.preventDefault();
    datTuKhoa(oTim.trim());
    datTrang(1);
  };

  const xoaLoc = (): void => {
    datNguonId('');
    datOTim('');
    datTuKhoa('');
    datTrangThai('');
    datTuNgay('');
    datDenNgay('');
    datTrang(1);
  };

  const xacNhanHuy = useCallback(async (): Promise<void> => {
    const l = dangHuy;
    datDangHuy(null);
    if (!l) return;
    try {
      await api.huy(l.id);
      datThongBao({ loai: 'ok', noiDung: dienVao(t.huyThanhCong, { ten: l.tenLichPhat }) });
      kq.taiLai();
    } catch (e) {
      datThongBao({
        loai: 'loi',
        noiDung: dienVao(t.huyLoi, {
          loi: e instanceof LoiApi || e instanceof Error ? e.message : String(e),
        }),
      });
    }
  }, [api, dangHuy, kq]);

  const [dangGuiLai, datDangGuiLai] = useState<string | null>(null);
  const guiLai = async (l: LichPhatNoiBoRa): Promise<void> => {
    if (!api.guiLai) return;
    datDangGuiLai(l.id);
    datThongBao(null);
    try {
      const r = await api.guiLai(l.id);
      datThongBao(
        r.soLoi > 0
          ? {
              loai: 'loi',
              noiDung: dienVao(t.phatCoLoi, { ten: l.tenLichPhat, loi: r.soLoi, chiTiet: r.loi.join('; ') }),
            }
          : {
              loai: 'ok',
              noiDung:
                r.soNhaCungCap > 0
                  ? dienVao(t.phatThanhCong, { ten: l.tenLichPhat, so: r.soNhaCungCap })
                  : dienVao(t.phatKhongCoNcc, { ten: l.tenLichPhat }),
            },
      );
    } catch (e) {
      datThongBao({
        loai: 'loi',
        noiDung: dienVao(t.phatLoi, {
          loi: e instanceof LoiApi || e instanceof Error ? e.message : String(e),
        }),
      });
    } finally {
      datDangGuiLai(null);
    }
  };

  const duocHuy = phien.coQuyen('schedule.delete');
  const duocSua = phien.coQuyen('schedule.update');
  const batDauStt = (trang - 1) * soDong;
  const dsDong = kq.duLieu?.items ?? [];

  const cot: CotBang<LichPhatNoiBoRa>[] = [
    {
      khoa: 'cham',
      nhan: t.cot.cham,
      canGiua: true,
      ve: (l) => {
        const hl = hieuLucCuaLich(l);
        return <span className={`dslp-cham dslp-cham--${hl}`} data-hieu-luc={hl} title={t.hieuLuc[hl]} />;
      },
    },
    {
      khoa: 'stt',
      nhan: t.cot.stt,
      canGiua: true,
      ve: (l) => batDauStt + dsDong.indexOf(l) + 1,
    },
    {
      khoa: 'ngayCapNhat',
      nhan: t.cot.ngayCapNhat,
      ve: (l) => <ONgayGio iso={l.updatedAt ?? l.createdAt} />,
    },
    { khoa: 'tenLich', nhan: t.cot.tenLich, ve: (l) => l.tenLichPhat },
    { khoa: 'trangThai', nhan: t.cot.trangThai, ve: (l) => <OTrangThai hl={hieuLucCuaLich(l)} /> },
    { khoa: 'diaBan', nhan: t.cot.diaBan, ve: tenDiaBanCuaLich },
    {
      khoa: 'thietBi',
      nhan: t.cot.thietBi,
      canGiua: true,
      ve: (l) => <OThietBi l={l} />,
    },
    {
      khoa: 'ngayPhat',
      nhan: t.cot.ngayPhat,
      ve: (l) => <OSoNgayPhat l={l} />,
    },
    { khoa: 'nguoiTao', nhan: t.cot.nguoiTao, ve: (l) => l.tenNguoiTao ?? '—' },
    {
      khoa: 'thaoTac',
      nhan: t.cot.thaoTac,
      // Lịch còn hiệu lực: Chỉnh sửa, Phát lịch (gửi lại DV002), Hủy lịch. Lịch đã hủy / hết
      // hiệu lực: chỉ Chỉnh sửa (lưu xong lịch trở lại hiệu lực với khung giờ mới).
      ve: (l) => {
        const conHieuLuc = hieuLucCuaLich(l) === 'con_hieu_luc';
        return (
          <span className="dslp-thao-tac">
            {duocSua ? (
              <Link
                to={`/lich-phat/${encodeURIComponent(l.id)}/sua`}
                className="nut nut--nho dslp-nut dslp-nut--sua"
                data-hanh-dong="sua-lich"
                aria-label={dienVao(t.suaNhan, { ten: l.tenLichPhat })}
              >
                <BieuTuong ten="quan-tri" co={16} />
                {t.sua}
              </Link>
            ) : null}
            {duocSua && conHieuLuc && api.guiLai ? (
              <button
                type="button"
                className="nut nut--nho dslp-nut dslp-nut--phat"
                data-hanh-dong="phat-lich"
                aria-label={dienVao(t.phatNhan, { ten: l.tenLichPhat })}
                disabled={dangGuiLai === l.id}
                onClick={() => void guiLai(l)}
              >
                <BieuTuong ten="phat" co={16} />
                {dangGuiLai === l.id ? t.dangPhat : t.phat}
              </button>
            ) : null}
            {duocHuy && conHieuLuc ? (
              <button
                type="button"
                className="nut nut--nho dslp-nut dslp-nut--huy"
                data-hanh-dong="huy-lich"
                aria-label={dienVao(t.huyNhan, { ten: l.tenLichPhat })}
                onClick={() => datDangHuy(l)}
              >
                <BieuTuong ten="tam-dung" co={16} />
                {t.huy}
              </button>
            ) : null}
          </span>
        );
      },
    },
  ];

  const heQuaHuy = (l: LichPhatNoiBoRa): string => {
    const soTb = soThietBiCuaLich(l);
    return dienVao(t.huyHeQua, {
      ten: l.tenLichPhat,
      soNgay: soNgayCuaLich(l),
      thietBi:
        soTb === 0
          ? dienVao(t.huyTatCaThietBi, { diaBan: tenDiaBanCuaLich(l) })
          : dienVao(t.huySoThietBi, { so: soTb }),
    });
  };

  return (
    <section className="man-hinh dslp" data-man-hinh="danh-sach-lich-phat">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>

      <div className="dslp-loc" role="search">
        <TruongLoc nhan={t.diaBan} htmlFor="dslp-dia-ban">
          <ChonDiaBanCay
            id="dslp-dia-ban"
            className="dslp-cay"
            nhan={t.diaBan}
            giaTri={nguonId}
            cay={cay.duLieu ?? []}
            nhanTatCa={t.toanTinh}
            goiYTim={t.timDiaBan}
            onDoi={doiLoc(datNguonId)}
          />
        </TruongLoc>
        <OTimTenLich id="dslp-ten" giaTri={oTim} onDoi={datOTim} onTim={tim} />
        <TruongLoc nhan={t.trangThai} htmlFor="dslp-trang-thai">
          <select
            id="dslp-trang-thai"
            value={trangThai}
            onChange={(e) => doiLoc(datTrangThai)(e.target.value as HieuLucLich | '')}
          >
            <option value="">{t.chonTrangThai}</option>
            {DS_HIEU_LUC.map((h) => (
              <option key={h} value={h}>
                {t.hieuLuc[h]}
              </option>
            ))}
          </select>
        </TruongLoc>
        <OKhoangNgay
          tienTo="dslp"
          tuNgay={tuNgay}
          denNgay={denNgay}
          onDoiTu={doiLoc(datTuNgay)}
          onDoiDen={doiLoc(datDenNgay)}
        />
      </div>

      <div className="dslp-hanh-dong">
        <Link to="/lich-phat/moi" className="nut nut--chinh" data-hanh-dong="them-moi-lich">
          + {t.themMoi}
        </Link>
      </div>

      {thongBao ? (
        <p
          className={`dai-thong-bao${thongBao.loai === 'loi' ? ' dai-thong-bao--loi' : ''}`}
          role="status"
          data-vung="thong-bao-huy-lich"
        >
          {thongBao.noiDung}
        </p>
      ) : null}

      <BangDuLieu<LichPhatNoiBoRa>
        nhan={t.tieuDe}
        cot={cot}
        dong={dsDong}
        khoaDong={(l) => l.id}
        trangThai={kq.trangThai}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={kq.taiLai}
        tong={kq.duLieu?.total ?? 0}
        trang={trang}
        soDongMoiTrang={soDong}
        onDoiTrang={datTrang}
        onDoiSoDong={(s) => {
          datSoDong(s);
          datTrang(1);
        }}
        dangLoc={dangLoc}
        onXoaLoc={xoaLoc}
        moTaRong={t.rongMoTa}
      />

      {dangHuy ? (
        <HopXacNhan
          tieuDe={t.huyTieuDe}
          heQua={heQuaHuy(dangHuy)}
          nhanThucHien={t.huyThucHien}
          onHuy={() => datDangHuy(null)}
          onThucHien={() => void xacNhanHuy()}
        />
      ) : null}
    </section>
  );
}
